import * as React from 'react';
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Route, Switch } from 'react-router-dom';
import { useHistory, useRoutePath } from '@shared/routing';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import MediaQuery from 'react-responsive';
import { ApolloError } from 'apollo-boost';

import {
	CheckinOrderFragment,
	CheckinOrderStatus,
	Customer,
	TravellerFieldEnum,
	useCheckinCompleteMutation,
	useGetCheckinOrderQuery,
	useUpdateCheckinOrderMutation
} from '@websky/graphql';
import PromoLoader from '../../../../../PromoLoader/components/PromoLoader';
import Stepbar from '../../../../../BaseComponents/Stepbar';
import {
	getCheckinHasCompleted,
	getCheckinIsBlockedForPayment,
	getCheckinOrder,
	getIsCheckinCompletedOrPreseating,
	getIsContactsInfoEmpty,
	getIsSeatMapUnavailable,
	hasBaggageServices,
	hasMealServices,
	hasSeatMap,
	hasUpgradeServices,
	hasInsuranceSeparateStep,
	isExpired,
	hasExtrasStepServices
} from '../../../store/order/selectors';
import { Steps } from '../../../../../BaseComponents/Stepbar/types';
import { useCheckinAvailabilityInfo, useSteps } from '../../../utils';
import { CheckinStep } from '../../../types';
import PaymentTimeLimitPage from '../../../../../PaymentTimeLimitPage';
import { setCheckinOrder } from '../../../store/order/actions';
import { useTheme } from '../../../../../theme';
import ProtectedRoute from '../../../../../ProtectedRoute/ProtectedRoute';
import { getUserValue, seatNumberRegex, TABLET_MIDDLE_WIDTH } from '../../../../../utils';
import { Contacts } from '../../../../../CustomerContactsForm/CustomerContactsForm';
import CustomerContactsModal from '../../../../../CustomerContactsForm/Modal/CustomerContactsModal';
import { useCustomerContactsForm } from '../../../../../CustomerContactsForm/hooks';
import { getIsServicesSaving } from '../../../store/servicesLoading/selectors';
import {
	getRestrictionsAccepted,
	getRestrictionsLoading,
	hasRestrictions as hasRestrictionsSelector
} from '@entities/restrictions';
import SimpleLoader from '../../../../../SimpleLoader';
import WarningModal from '../../../../../WarningModal';
import { GraphQLError } from 'graphql';
import MobileStepbar from '../../../../../MobileStepbar';
import { useCheckinOrderServices } from '../../../hooks';
import { useStepsToStepbarStepsAdapter } from './utils';
import { CheckinUnavailableSeatMapError } from '../CheckinErrorBoundary/CheckinErrors';
import UnconfirmedServicesModal from './UnconfirmedServicesModal/UnconfirmedServicesModal';
import { getUnconfirmedServices, hasRequestedServices } from './UnconfirmedServicesModal/utils';
import { useUnconfirmedServicesModal } from './UnconfirmedServicesModal/hooks';
import { useRenderers } from '../../../../../renderProps';
import { useCheckinPaymentFailedHandlers, useCheckinPaymentSuccessHandlers } from './hooks';
import { PaymentFlowErrorModal, PaymentResultFlow } from '@features/payment';

const Upgrade = lazy(() => import('./routes/Upgrade'));
const Meal = lazy(() => import('./routes/Meal'));
const Baggage = lazy(() => import('./routes/Baggage'));
const Restrictions = lazy(() => import('./routes/Restrictions'));
const Payment = lazy(() => import('./routes/Payment'));
const Extras = lazy(() => import('./routes/Extras'));
const Insurance = lazy(() => import('./routes/Insurance'));
const Passengers = lazy(() => import('./Passengers/Passengers'));
const PreselectedSeats = lazy(() => import('./PreselectedSeats/PreselectedSeats'));

const Order: React.FC = () => {
	const { t } = useTranslation('Checkin');
	const { Order: css } = useTheme('Checkin');
	const { CheckoutDesktopStepbar: CheckoutDesktopStepbarOverride = Stepbar } = useRenderers();

	const isCheckinCompleted = useSelector(getCheckinHasCompleted);
	const isBlockedForPayment = useSelector(getCheckinIsBlockedForPayment);
	const hasBaggage = useSelector(hasBaggageServices);
	const hasMeals = useSelector(hasMealServices);
	const hasSeats = useSelector(hasSeatMap);
	const hasUpgrade = useSelector(hasUpgradeServices);
	const hasExtras = useSelector(hasExtrasStepServices);
	const hasInsurance = useSelector(hasInsuranceSeparateStep);
	const hasRestrictions = useSelector(hasRestrictionsSelector);
	const restrictionsAccepted = useSelector(getRestrictionsAccepted);
	const restrictionsLoading = useSelector(getRestrictionsLoading);
	const isCheckinExpired = useSelector(isExpired);
	const order = useSelector(getCheckinOrder);
	const isSeatMapUnavailable = useSelector(getIsSeatMapUnavailable);
	const isContactsInfoEmpty = useSelector(getIsContactsInfoEmpty);
	const isServicesSaving = useSelector(getIsServicesSaving);
	const isCheckinCompletedOrPreseating = useSelector(getIsCheckinCompletedOrPreseating);

	const history = useHistory();
	const [requestError, setRequestError] = useState<string>();

	const {
		saveClientInfo,
		closeContactsModal,
		openContactsModal,
		isContactsModalOpen,
		loading: isClientInfoSaving
	} = useCustomerContactsForm();
	const {
		goToNextStep,
		setStep,
		getNextStep,
		availableSteps,
		previousStep,
		getNextStepEnum,
		getStepFromPathname
	} = useSteps();
	const {
		startPolling: startUpdateCheckinOrderPolling,
		setUnconfirmedServices,
		unconfirmedServices,
		removeServices: removeUnconfirmedServices
	} = useUnconfirmedServicesModal();

	const {
		location: { pathname }
	} = history;

	const {
		isLoading: isPaymentFailedFlowLoading,
		onMount: onMountPaymentFailedFlow,
		onMountThenCheckinCompleted: onMountThenCheckinCompletedPaymentFailedFlow,
		onFlowStart: onFlowStartPaymentFailedFlow,
		goToPaymentAfterFailedPayment,
		goToOrderAfterFailedPayment,
		...paymentFailedFlowErrorState
	} = useCheckinPaymentFailedHandlers({ setStep, goToNextStep });

	const {
		isLoading: isPaymentSuccessFlowLoading,
		onMount: onMountPaymentSuccessFlow,
		onMountThenCheckinCompleted: onMountThenCheckinCompletedPaymentSuccessFlow,
		onFlowStart: onFlowStartPaymentSuccessFlow,
		onFlowAbort: onFlowAbortPaymentSuccessFlow,
		goToOrderAfterSuccessPayment,
		...paymentSuccessFlowErrorState
	} = useCheckinPaymentSuccessHandlers({
		setStep,
		goToNextStep
	});

	const handleReturn = useCallback(() => {
		history.push('/');
	}, []);

	const { showExpireModal } = useCheckinAvailabilityInfo(600000);

	const { refetch: getOrder } = useGetCheckinOrderQuery({ skip: true, fetchPolicy: 'no-cache' });
	const [completeCheckin] = useCheckinCompleteMutation();
	const [updateCheckinOrder] = useUpdateCheckinOrderMutation();

	const currentStep = useMemo(() => getStepFromPathname(pathname), [pathname, getStepFromPathname]);

	const isSeatsStep = currentStep === CheckinStep.Seats;
	const isPaymentStep = currentStep === CheckinStep.Payment;

	const saveClientInfoAndLoadOrder = async (data: Contacts) => {
		await saveClientInfo({ variables: { ...data, orderId: order.id } });
		const { data: getOrderResult } = await getOrder({ id: order.id });

		if (getOrderResult?.CheckinOrder) {
			setOrder(getOrderResult.CheckinOrder);
		}

		closeContactsModal();

		if (isPaymentStep) {
			previousStep(CheckinStep.Payment);
		} else {
			goToNextStep(currentStep);
		}
	};

	const dispatch = useDispatch();

	const setOrder = useCallback(
		(order: CheckinOrderFragment) => {
			dispatch(setCheckinOrder(order));
		},
		[dispatch]
	);

	const onCloseWarningModal = () => {
		setRequestError(null);
	};

	const onRequestError = (errors: any) => {
		const error =
			errors?.graphQLErrors?.[0]?.debugMessage || errors?.graphQLErrors?.[0]?.message || 'Checkin error';
		const invalidSeatError = 'Invalid seat number';

		if (error.includes(invalidSeatError)) {
			setRequestError(t(invalidSeatError, { seatNumber: error.match(seatNumberRegex)[0] }));
		} else {
			setRequestError(t(error));
		}
	};

	const steps = useStepsToStepbarStepsAdapter(availableSteps);

	const getStepIndexByPathname = () =>
		Object.keys(steps).findIndex(stepKey => {
			return stepKey && pathname.includes(stepKey);
		});

	const [activeStepIndex, setActiveStepIndex] = useState(getStepIndexByPathname());

	const activeStep = steps[Object.keys(steps)[activeStepIndex]];

	useEffect(() => {
		const filteredSteps: Steps = {};

		if (!isCheckinCompleted && isBlockedForPayment && !isPaymentStep) {
			setStep(CheckinStep.Payment);
		}

		Object.keys(steps).forEach(stepKey => {
			if (steps.hasOwnProperty(stepKey) && !steps[stepKey].isHidden) {
				filteredSteps[stepKey] = steps[stepKey];
			}
		});

		if (!isCheckinCompleted && /^\/(\d+)\/?$/.test(pathname)) {
			setActiveStepIndex(0); // passengers
		} else if (isCheckinCompleted) {
			setActiveStepIndex(Object.keys(filteredSteps).length - 1); // boarding pass
		} else {
			setActiveStepIndex(getStepIndexByPathname());
		}
	}, [pathname, steps]);

	const onRefetchOrder = useCallback(async () => {
		try {
			const { data } = await getOrder({ id: order.id });

			if (data?.CheckinOrder) {
				if (data.CheckinOrder.status !== CheckinOrderStatus.Started) {
					throw new Error('Wrong order status!');
				}

				setOrder(data.CheckinOrder);
			}

			return data.CheckinOrder;
		} catch (e) {
			if (e instanceof ApolloError) {
				if (e.graphQLErrors?.length) {
					onRequestError(e);
				}
			} else {
				throw e;
			}

			return null;
		}
	}, [order.id, getOrder]);

	const { onDeleteItem, isLoading: cartLoading } = useCheckinOrderServices(onRefetchOrder, onRequestError, setStep);

	const onProceed = useCallback(async () => {
		if (currentStep === undefined) {
			return;
		}

		const nextStep = getNextStepEnum(currentStep);

		const checkinOrder = await onRefetchOrder();

		if (nextStep === CheckinStep.Payment) {
			const unconfirmedServices = getUnconfirmedServices(checkinOrder);

			if (unconfirmedServices.size) {
				setUnconfirmedServices(unconfirmedServices);

				if (hasRequestedServices(unconfirmedServices)) {
					startUpdateCheckinOrderPolling(onProceed);
				}

				return null;
			} else {
				setUnconfirmedServices(null);
			}

			// Перед оплатой перевалидируем доступность регистрации по сегментам: МПС игнорирует протухший таймлимит.
			const updateResult = await updateCheckinOrder({ variables: { id: checkinOrder.id } });
			const orderForPayment = updateResult?.data?.UpdateCheckinOrder ?? checkinOrder;

			setOrder(orderForPayment);

			if (orderForPayment.status !== CheckinOrderStatus.Started) {
				return null;
			}

			const paymentIsRequired = orderForPayment.paymentIsRequired;
			const isContactsInfoEmpty = orderForPayment.customer.contactInfoIsEmpty;

			if (paymentIsRequired) {
				if (isContactsInfoEmpty) {
					openContactsModal();
				} else {
					setStep(CheckinStep.Payment);
				}
			} else {
				try {
					const { data, errors } = await completeCheckin({
						variables: {
							id: orderForPayment.id
						}
					});
					if (errors) {
						console.error(errors);
						onRequestError(errors);
					} else if (data?.CheckinComplete) {
						setOrder(data.CheckinComplete);
						setStep(CheckinStep.Passengers);
					}
				} catch (e) {
					onRequestError(e as GraphQLError[]);
					console.error(e);
				}
			}
		} else {
			goToNextStep(currentStep);
		}
	}, [
		currentStep,
		getNextStepEnum,
		goToNextStep,
		setStep,
		onRefetchOrder,
		onRequestError,
		openContactsModal,
		startUpdateCheckinOrderPolling,
		completeCheckin,
		updateCheckinOrder,
		setOrder,
		setUnconfirmedServices
	]);

	const onCheckinStart = useCallback(() => {
		goToNextStep(CheckinStep.Passengers);
	}, []);

	const customerValues = React.useMemo(() => {
		const customer = order.customer as Customer;

		return {
			email: getUserValue(customer, TravellerFieldEnum.Email),
			phone: getUserValue(customer, TravellerFieldEnum.Phone)
		};
	}, [order.customer]);

	useEffect(() => {
		if (isSeatsStep && isSeatMapUnavailable) {
			throw new CheckinUnavailableSeatMapError(
				t(
					'Service is temporarily unavailable. There are no seats available. Please try again later, or check in at the airport.'
				)
			);
		}
	}, [isSeatsStep, isSeatMapUnavailable]);

	const routePath = useRoutePath();

	if (restrictionsLoading) {
		return <PromoLoader innerPromoLoader={true} />;
	}

	return (
		<div className={css.container}>
			{(isServicesSaving || isPaymentFailedFlowLoading || isPaymentSuccessFlowLoading || cartLoading) && (
				<SimpleLoader />
			)}

			{!showExpireModal && !isCheckinExpired && (
				<>
					{!isCheckinCompleted && (
						<>
							<MediaQuery minWidth={TABLET_MIDDLE_WIDTH + 1}>
								<CheckoutDesktopStepbarOverride
									className={css.stepBar}
									steps={steps}
									activeStep={activeStep}
								/>
							</MediaQuery>

							<MediaQuery maxWidth={TABLET_MIDDLE_WIDTH}>
								<MobileStepbar
									order={order}
									currentStep={activeStepIndex}
									stepbarConfig={steps}
									onServiceDelete={onDeleteItem}
								/>
							</MediaQuery>
						</>
					)}

					<CustomerContactsModal
						initialValues={customerValues}
						onContactsSubmit={saveClientInfoAndLoadOrder}
						isLoading={isClientInfoSaving}
						isOpen={isContactsModalOpen}
					/>

					<UnconfirmedServicesModal
						open={!!unconfirmedServices?.size}
						unconfirmedServices={unconfirmedServices}
						order={order}
						onRemove={services => removeUnconfirmedServices(services, onProceed)}
						isServicesSaving={isServicesSaving}
					/>

					<Suspense fallback={<PromoLoader />}>
						<Switch>
							<Route
								path={routePath('/:id(\\d+)')}
								exact
								render={() => (
									<Passengers onCheckinStart={onCheckinStart} onRefetchOrder={onRefetchOrder} />
								)}
							/>

							<ProtectedRoute
								path={routePath('/:id(\\d+)/upgrade')}
								isAccessible={!isCheckinCompletedOrPreseating && hasUpgrade}
								redirectTo={getNextStep(CheckinStep.Upgrade)}
								render={() => <Upgrade />}
							/>

							<ProtectedRoute
								path={routePath('/:id(\\d+)/baggage')}
								isAccessible={!isCheckinCompletedOrPreseating && hasBaggage}
								redirectTo={
									isCheckinCompletedOrPreseating
										? getNextStep(CheckinStep.Passengers)
										: !hasBaggage
										? getNextStep(CheckinStep.Baggage)
										: ''
								}
								render={() => <Baggage />}
							/>

							<ProtectedRoute
								path={routePath('/:id(\\d+)/meal')}
								isAccessible={!isCheckinCompletedOrPreseating && hasMeals}
								redirectTo={
									isCheckinCompletedOrPreseating
										? getNextStep(CheckinStep.Passengers)
										: !hasMeals
										? getNextStep(CheckinStep.Meal)
										: ''
								}
								render={() => <Meal isLoading={isServicesSaving} />}
							/>

							<ProtectedRoute
								path={routePath('/:id(\\d+)/seats')}
								isAccessible={!isCheckinCompletedOrPreseating && hasSeats}
								redirectTo={getNextStep(CheckinStep.Passengers)}
								render={() => <PreselectedSeats onProceed={onProceed} />}
							/>

							<ProtectedRoute
								path={routePath('/:id(\\d+)/insurance')}
								isAccessible={!isCheckinCompletedOrPreseating && hasInsurance}
								redirectTo={
									isCheckinCompletedOrPreseating
										? getNextStep(CheckinStep.Passengers)
										: !hasInsurance
										? getNextStep(CheckinStep.Insurance)
										: ''
								}
								render={() => <Insurance onProceed={onProceed} />}
							/>

							<ProtectedRoute
								path={routePath('/:id(\\d+)/extras')}
								isAccessible={!isCheckinCompletedOrPreseating && hasExtras}
								redirectTo={
									isCheckinCompletedOrPreseating
										? getNextStep(CheckinStep.Passengers)
										: !hasExtras
										? getNextStep(CheckinStep.Extras)
										: ''
								}
								render={() => <Extras onProceed={onProceed} />}
							/>

							<ProtectedRoute
								path={routePath('/:id(\\d+)/restrictions')}
								isAccessible={!isCheckinCompletedOrPreseating && hasRestrictions}
								redirectTo={
									isCheckinCompletedOrPreseating
										? getNextStep(CheckinStep.Passengers)
										: !hasRestrictions
										? getNextStep(CheckinStep.Restrictions)
										: ''
								}
								render={() => <Restrictions onProceed={onProceed} />}
							/>

							<Route
								exact
								path={routePath('/:id(\\d+)/payment')}
								render={() => {
									/**
									 * если есть ограничения и пользователь не принял их, переходим на страницу ограничений
									 */
									if (hasRestrictions && !restrictionsAccepted) {
										setStep(CheckinStep.Restrictions);

										return null;
									}

									if (isCheckinCompleted) {
										setStep(CheckinStep.Passengers);

										return null;
									}

									if (isContactsInfoEmpty) {
										openContactsModal();

										return null;
									}

									return (
										<Payment
											onBackToOrder={goToOrderAfterFailedPayment}
											paymentResetLoading={isPaymentFailedFlowLoading}
										/>
									);
								}}
							/>

							<Route
								exact
								path={routePath('/:id(\\d+)/payment/failed')}
								render={() => {
									if (isCheckinCompleted) {
										return (
											<PaymentResultFlow
												key="checkin-payment-failed-completed"
												result="fail"
												onMount={onMountThenCheckinCompletedPaymentFailedFlow}
											/>
										);
									}

									return (
										<PaymentResultFlow
											key="checkin-payment-failed-reset"
											result="fail"
											isLoading={isPaymentFailedFlowLoading}
											onFlowStart={onFlowStartPaymentFailedFlow}
											onMount={onMountPaymentFailedFlow}
											onClick={goToPaymentAfterFailedPayment}
											onBackToOrder={goToOrderAfterFailedPayment}
										/>
									);
								}}
							/>

							<Route
								exact
								path={routePath('/:id(\\d+)/payment/successfull')}
								render={() => {
									if (isCheckinCompleted) {
										return (
											<PaymentResultFlow
												key="checkin-payment-successfull-completed"
												result="success"
												onMount={onMountThenCheckinCompletedPaymentSuccessFlow}
											/>
										);
									}

									return (
										<PaymentResultFlow
											key="checkin-payment-successfull-reset"
											result="success"
											isLoading={isPaymentSuccessFlowLoading}
											onMount={onMountPaymentSuccessFlow}
											onFlowStart={onFlowStartPaymentSuccessFlow}
											onFlowAbort={onFlowAbortPaymentSuccessFlow}
											onClick={goToOrderAfterSuccessPayment}
											onClose={goToOrderAfterSuccessPayment}
										/>
									);
								}}
							/>
						</Switch>
					</Suspense>
				</>
			)}

			{(showExpireModal || isCheckinExpired) && (
				<div className={css.timelimit__wrp}>
					<PaymentTimeLimitPage
						title={t('Sorry,')}
						text={t('online check-in is closed for this flight')}
						buttonText={t('OK')}
						onClick={handleReturn}
					/>
				</div>
			)}

			<WarningModal
				title={t('Oops, something went wrong')}
				content={t(
					'An error occurred during the check-in process. Please try again later or contact a customer support service.'
				)}
				isOpen={!!requestError}
				errorMessage={requestError}
				buttonText={t('Close')}
				onButtonClick={onCloseWarningModal}
			/>

			<PaymentFlowErrorModal
				pathname={pathname}
				baseUrl={`/${order.id}`}
				failedFlow={paymentFailedFlowErrorState}
				successFlow={paymentSuccessFlowErrorState}
				paymentFlow={{
					...paymentFailedFlowErrorState,
					onErrorConfirm: goToOrderAfterFailedPayment
				}}
			/>
		</div>
	);
};

export default Order;
